/**
 * Pieces shared by the University pages: inline text formatting, reading links,
 * and the attribute card.
 */

import { ExternalLink, TriangleAlert } from 'lucide-react';
import { Badge } from './ui/badge';

/** Renders the only formatting course text uses: `code` and **bold**. */
export function RichText({ text }) {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, i) => {
    if (part.startsWith('`')) {
      return (
        <code key={i} className="font-mono text-[0.85em] bg-muted rounded px-1 py-0.5">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('**')) {
      return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

export function ReadingLink({ link }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-start gap-1.5 text-xs text-primary hover:text-primary/80"
    >
      <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
      <span>
        {link.title}
        <span className="text-muted-foreground"> · {link.source}</span>
      </span>
    </a>
  );
}

// What the attribute's value looks like, in the words the builder uses.
const VALUE_LABEL = {
  numeric: 'Number',
  boolean: 'True or false',
  event: 'Bars since an event',
};

const BUILDER_LABEL = {
  available: { text: 'In the builder', variant: 'win' },
  python_only: { text: 'Python only', variant: 'outline' },
  fundamentals_off: { text: 'Builder: off for now', variant: 'outline' },
};

function valueSummary(kinds) {
  const keys = Object.keys(kinds);
  if (keys.length === 1 && keys[0] === '') {
    return VALUE_LABEL[kinds['']] || 'Text label';
  }
  return `Sub-values: ${keys.join(', ')}`;
}

function paramSummary(params) {
  return params
    .map((p) => (p.default === null ? p.name : `${p.name} = ${String(p.default)}`))
    .join(', ');
}

function CardRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
        {label}
      </div>
      <p className="text-sm leading-relaxed text-foreground/90">{children}</p>
    </div>
  );
}

export function AttributeCard({ card }) {
  const builder = BUILDER_LABEL[card.builder];
  const notes = [];
  if (card.caveat) notes.push(card.caveat);
  if (card.history === 'snapshot') {
    notes.push('Snapshot-only: this has no value before the first stored snapshot, so backtests over earlier dates have nothing to read.');
  }
  if (card.history === 'backfill') {
    notes.push('Comes from the historical backfill, so check that it is populated for your universe and dates.');
  }
  // CFOQuality says this in its own caveat, so don't repeat it there.
  if (card.missing_data === 'skips' && !card.caveat) {
    notes.push('When its inputs are missing this attribute is not ready, so the screener skips the stock, even if the group is optional.');
  }

  return (
    <div id={`attr-${card.alias}`} className="rounded-lg border border-border bg-card p-4 scroll-mt-16">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className="font-mono text-sm font-semibold">{card.alias}</h3>
        <Badge variant={builder.variant}>{builder.text}</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        {valueSummary(card.value_kinds)}
        {card.params.length > 0 && <> · {paramSummary(card.params)}</>}
      </p>

      <div className="flex flex-col gap-3">
        <CardRow label="What it is"><RichText text={card.what} /></CardRow>
        <CardRow label="Why it matters in a screener"><RichText text={card.why} /></CardRow>
        <CardRow label="How to read it"><RichText text={card.read} /></CardRow>
        <CardRow label="Example condition"><RichText text={card.example} /></CardRow>
      </div>

      {notes.map((note) => (
        <p key={note} className="mt-3 flex gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs leading-relaxed text-amber-700 dark:text-amber-300">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span><RichText text={note} /></span>
        </p>
      ))}

      {card.links.length > 0 && (
        <div className="mt-3 flex flex-col gap-1">
          {card.links.map((link) => <ReadingLink key={link.key} link={link} />)}
        </div>
      )}
    </div>
  );
}
